import React, { useEffect, useRef, useCallback } from 'react';

const NEON_COLORS = [
  '#00F0FF', '#00F0FF', // cyan (more weight)
  '#FF00E5', '#FF00E5', // magenta
  '#00FF87',            // green
  '#FFE600',            // yellow
  '#2979FF',            // blue
  '#FFFFFF',            // white sparks
];

const NeonConfetti = ({ active, duration = 4000, particleCount = 120, onComplete }) => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const startTimeRef = useRef(null);

  const createParticle = useCallback((w, h) => {
    const color = NEON_COLORS[Math.floor(Math.random() * NEON_COLORS.length)];
    const isGlitter = Math.random() > 0.6;
    return {
      x: Math.random() * w,
      y: -10 - Math.random() * h * 0.3,
      vx: (Math.random() - 0.5) * 4,
      vy: 1.5 + Math.random() * 4,
      size: isGlitter ? 2 + Math.random() * 3 : 4 + Math.random() * 8,
      color,
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 8,
      opacity: 0.8 + Math.random() * 0.2,
      shape: isGlitter ? 'circle' : Math.random() > 0.5 ? 'rect' : 'strip',
      wobble: Math.random() * Math.PI * 2,
      wobbleSpeed: 0.02 + Math.random() * 0.04,
    };
  }, []);

  useEffect(() => {
    if (!active) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const particles = [];
    for (let i = 0; i < particleCount; i++) {
      particles.push(createParticle(canvas.width, canvas.height));
    }

    startTimeRef.current = performance.now();

    const animate = (now) => {
      const elapsed = now - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (const p of particles) {
        p.x += p.vx + Math.sin(p.wobble) * 0.5;
        p.y += p.vy;
        p.vy += 0.04; // gravity
        p.vx *= 0.99;
        p.rotation += p.rotationSpeed;
        p.wobble += p.wobbleSpeed;

        // Fade out in last 30%
        const fadeStart = 0.7;
        const alpha = progress > fadeStart
          ? p.opacity * (1 - (progress - fadeStart) / (1 - fadeStart))
          : p.opacity;

        if (alpha <= 0) continue;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.globalAlpha = alpha;

        // Neon glow
        ctx.shadowColor = p.color;
        ctx.shadowBlur = p.shape === 'circle' ? 8 : 4;
        ctx.fillStyle = p.color;

        if (p.shape === 'circle') {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        } else if (p.shape === 'rect') {
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        } else {
          ctx.fillRect(-p.size / 2, -1, p.size, 2.5);
        }

        ctx.restore();
      }

      if (progress < 1) {
        animRef.current = requestAnimationFrame(animate);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        onComplete?.();
      }
    };

    animRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resize);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [active, duration, particleCount, createParticle, onComplete]);

  if (!active) return null;

  return (
    <div>
      <canvas
        ref={canvasRef}
        className="fixed inset-0 z-[9999] pointer-events-none"
        data-testid="neon-confetti"
      />
    </div>
  );
};

export default NeonConfetti;
